import { useCallback, useState } from 'react';
import { UseFormReset } from 'react-hook-form';

import { InsuranceForm } from '../Dashboard/Insurance/utils/types';
import { useBlocker } from './useBlocker';

interface UnsavedChangesWarningReturn {
  showModal: boolean;
  confirmNavigation: () => void;
  saveChanges: () => void;
  hideModal: () => void;
  handleBlockedNavigation: (callback: () => void) => void;
}

export function useUnsavedChangesWarning(
  isDirty: boolean,
  formRef: React.RefObject<HTMLFormElement>,
  reset?: UseFormReset<InsuranceForm>,
  onDiscard?: () => void,
): UnsavedChangesWarningReturn {
  const [blockerTx, setBlockerTx] = useState<null | { retry: () => void }>(null);
  const [manualConfirmCallback, setManualConfirmCallback] = useState<null | (() => void)>(null);

  const blocker = useCallback((tx: { retry: () => void }) => {
    setBlockerTx(tx);
  }, []);

  useBlocker(blocker, isDirty);

  const showModal = blockerTx !== null || manualConfirmCallback !== null;

  const confirmNavigation = (): void => {
    reset && reset();
    onDiscard && onDiscard();

    if (blockerTx) {
      blockerTx.retry();
      setBlockerTx(null);
    }

    if (manualConfirmCallback) {
      manualConfirmCallback();
      setManualConfirmCallback(null);
    }
  };

  const saveChanges = (): void => {
    if (isDirty && formRef?.current) {
      formRef.current.requestSubmit();
    }
    setBlockerTx(null);
    setManualConfirmCallback(null);
  };

  const hideModal = (): void => {
    setBlockerTx(null);
    setManualConfirmCallback(null);
  };

  const handleBlockedNavigation = (callback: () => void): void => {
    if (isDirty) {
      setManualConfirmCallback(() => callback);
    } else {
      callback();
    }
  };

  return {
    showModal,
    confirmNavigation,
    saveChanges,
    hideModal,
    handleBlockedNavigation,
  };
}
